import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminLogin,
  adminGetBuildings,
  adminCreateBuilding,
  adminUpdateBuilding,
  adminDeleteBuilding,
  adminReseed,
} from "@/lib/api";
import { COLOR_PRESETS, ACCENT_PRESETS, SHAPE_OPTIONS } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, RefreshCw, LogOut, ExternalLink, Lock } from "lucide-react";

const TOKEN_KEY = "sm_admin_token";

const EMPTY = {
  slug: "",
  name: "",
  category: "",
  description: "",
  learnMoreUrl: "",
  color: "#2B2E34",
  accent: "#FFCE24",
  shape: "tower",
  positionX: 0,
  positionZ: 0,
  height: 8,
  width: 4,
  depth: 4,
  order: 0,
  isActive: true,
};

function LoginView({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, token);
      toast.success("Welcome back");
      onAuthed(token);
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#E9E7E0" }}>
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border bg-white p-6"
        style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "var(--shadow-panel)" }}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md" style={{ background: "#2B2E34" }}>
            <Lock size={16} color="#FFCE24" />
          </span>
          <div>
            <div className="font-display font-bold uppercase tracking-tight text-2xl text-[#2B2E34] leading-none">
              S<span style={{ color: "#FFCE24" }}>/</span>M Admin
            </div>
            <div className="font-display uppercase tracking-[0.16em] text-[10px] text-[#848484]">Manage the world</div>
          </div>
        </div>
        <div className="mt-5 space-y-2">
          <Label htmlFor="pw" className="font-display uppercase tracking-wide text-xs">Password</Label>
          <Input
            id="pw"
            data-testid="admin-token-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            autoComplete="current-password"
          />
        </div>
        <Button data-testid="admin-login-button" type="submit" disabled={loading} className="mt-4 w-full font-display uppercase tracking-wide">
          {loading ? "Checking…" : "Enter"}
        </Button>
        <Link to="/" className="mt-4 block text-center font-display uppercase tracking-wide text-xs text-[#848484] hover:text-[#2B2E34]">
          ← Back to world
        </Link>
      </form>
    </div>
  );
}

function ColorSwatch({ value }) {
  return <span className="inline-block h-4 w-4 rounded border align-middle" style={{ background: value, borderColor: "rgba(0,0,0,0.2)" }} />;
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, obj=edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const authed = !!token;

  const load = async (tk) => {
    setLoading(true);
    try {
      const data = await adminGetBuildings(tk || token);
      setBuildings(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired, please log in");
      } else {
        toast.error("Failed to load buildings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, order: buildings.length });
    setSheetOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...EMPTY, ...b });
    setSheetOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.slug.trim()) return "Slug is required";
    if (!form.category.trim()) return "Category is required";
    if (form.learnMoreUrl && !/^https?:\/\//i.test(form.learnMoreUrl)) return "Learn-more URL must start with http(s)://";
    if ([form.positionX, form.positionZ, form.height, form.width, form.depth].some((n) => Number.isNaN(Number(n))))
      return "Position/size values must be numbers";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      positionX: Number(form.positionX),
      positionZ: Number(form.positionZ),
      height: Number(form.height),
      width: Number(form.width),
      depth: Number(form.depth),
      order: Number(form.order),
    };
    try {
      if (editing) {
        await adminUpdateBuilding(token, editing.id, payload);
        toast.success("Building updated");
      } else {
        await adminCreateBuilding(token, payload);
        toast.success("Building created");
      }
      setSheetOpen(false);
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? This cannot be undone.`)) return;
    try {
      await adminDeleteBuilding(token, b.id);
      toast.success("Building deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const reseed = async () => {
    if (!window.confirm("Reset to the default 7 areas? This replaces all current buildings.")) return;
    try {
      await adminReseed(token);
      toast.success("World reset to defaults");
      await load();
    } catch (e) {
      toast.error("Reseed failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const colorOptions = useMemo(() => COLOR_PRESETS, []);
  const accentOptions = useMemo(() => ACCENT_PRESETS, []);

  if (!authed) return <LoginView onAuthed={setToken} />;

  return (
    <div className="min-h-screen" style={{ background: "#E9E7E0" }}>
      {/* header */}
      <header className="sticky top-0 z-10" style={{ background: "#2B2E34", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="absolute top-0 left-0 right-0" style={{ height: 3, background: "#FFCE24" }} />
        <div className="mx-auto max-w-[1280px] px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display font-bold text-white text-2xl leading-none">S<span style={{ color: "#FFCE24" }}>/</span>M</span>
            <span className="font-display uppercase tracking-[0.16em] text-[11px] text-white/55">World Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="inline-flex items-center gap-1.5 rounded-[10px] px-3 py-2 text-sm font-display font-semibold uppercase tracking-wide text-white hover:bg-white/10 transition-colors">
              <ExternalLink size={15} /> <span className="hidden sm:inline">View world</span>
            </Link>
            <Button onClick={reseed} variant="outline" className="font-display uppercase tracking-wide bg-transparent text-white border-white/20 hover:bg-white/10 hover:text-white">
              <RefreshCw size={15} className="mr-1.5" /> <span className="hidden sm:inline">Reseed</span>
            </Button>
            <Button onClick={logout} className="font-display uppercase tracking-wide" style={{ background: "#FFCE24", color: "#2B2E34" }}>
              <LogOut size={15} className="mr-1.5" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] px-4 md:px-6 py-8">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display font-bold uppercase tracking-tight text-4xl text-[#2B2E34] leading-none">Buildings</h1>
            <p className="mt-1 font-body text-[#404040]">Edit the areas of the world. Changes appear live on the site.</p>
          </div>
          <Button data-testid="admin-create-building-button" onClick={openCreate} className="font-display uppercase tracking-wide">
            <Plus size={16} className="mr-1.5" /> New Building
          </Button>
        </div>

        <div className="mt-5 rounded-xl border bg-white overflow-hidden" style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "var(--shadow-panel)" }}>
          <div className="overflow-x-auto sm-scroll">
            <Table data-testid="admin-buildings-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Order</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Name</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Category</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Shape</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Colors</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Pos (x,z)</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs">Active</TableHead>
                  <TableHead className="font-display uppercase tracking-wide text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 font-body text-[#848484]">Loading…</TableCell>
                  </TableRow>
                )}
                {!loading && buildings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 font-body text-[#848484]">No buildings yet.</TableCell>
                  </TableRow>
                )}
                {!loading &&
                  buildings.map((b) => (
                    <TableRow key={b.id} className="hover:bg-black/[0.03]">
                      <TableCell className="font-mono-code text-sm">{b.order}</TableCell>
                      <TableCell className="font-display font-semibold text-[#2B2E34]">{b.name}</TableCell>
                      <TableCell className="font-body text-sm text-[#404040]">{b.category}</TableCell>
                      <TableCell className="font-mono-code text-xs uppercase">{b.shape}</TableCell>
                      <TableCell><span className="flex items-center gap-1.5"><ColorSwatch value={b.color} /><ColorSwatch value={b.accent} /></span></TableCell>
                      <TableCell className="font-mono-code text-xs">{b.positionX}, {b.positionZ}</TableCell>
                      <TableCell>
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-display uppercase tracking-wide ${b.isActive ? "text-[#2B2E34]" : "text-white"}`} style={{ background: b.isActive ? "#FFCE24" : "#848484" }}>
                          {b.isActive ? "Live" : "Hidden"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button data-testid={`admin-edit-building-button-${b.id}`} size="icon" variant="ghost" onClick={() => openEdit(b)} aria-label="Edit">
                            <Pencil size={16} />
                          </Button>
                          <Button data-testid={`admin-delete-building-button-${b.id}`} size="icon" variant="ghost" onClick={() => remove(b)} aria-label="Delete" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      {/* edit / create sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent data-testid="admin-building-sheet" className="w-full sm:max-w-md overflow-y-auto sm-scroll">
          <SheetHeader>
            <SheetTitle className="font-display uppercase tracking-tight text-2xl">
              {editing ? "Edit building" : "New building"}
            </SheetTitle>
            <SheetDescription className="font-body">
              Configure this area of the world. Changes appear live on the site.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4 pb-24">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Name</Label>
                <Input data-testid="building-form-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Branding" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Slug</Label>
                <Input data-testid="building-form-slug" value={form.slug} onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} placeholder="branding" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-display uppercase tracking-wide text-xs">Category</Label>
              <Input data-testid="building-form-category" value={form.category} onChange={(e) => setField("category", e.target.value)} placeholder="Brand Foundations" />
            </div>

            <div className="space-y-1.5">
              <Label className="font-display uppercase tracking-wide text-xs">Description</Label>
              <Textarea data-testid="building-form-description" rows={4} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="What this area is about…" />
            </div>

            <div className="space-y-1.5">
              <Label className="font-display uppercase tracking-wide text-xs">Learn-more URL</Label>
              <Input data-testid="building-form-url" value={form.learnMoreUrl} onChange={(e) => setField("learnMoreUrl", e.target.value)} placeholder="https://simon-myers.com/services/…" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Shape</Label>
                <Select value={form.shape} onValueChange={(v) => setField("shape", v)}>
                  <SelectTrigger data-testid="building-form-shape"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHAPE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Body color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.color} onChange={(e) => setField("color", e.target.value)} className="h-9 w-10 rounded border" style={{ borderColor: "rgba(0,0,0,0.15)" }} />
                  <Select value={colorOptions.find((c) => c.value.toLowerCase() === (form.color || "").toLowerCase())?.value || ""} onValueChange={(v) => setField("color", v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Preset" /></SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Accent</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent} onChange={(e) => setField("accent", e.target.value)} className="h-9 w-10 rounded border" style={{ borderColor: "rgba(0,0,0,0.15)" }} />
                  <Select value={accentOptions.find((c) => c.value.toLowerCase() === (form.accent || "").toLowerCase())?.value || ""} onValueChange={(v) => setField("accent", v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Preset" /></SelectTrigger>
                    <SelectContent>
                      {accentOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Pos X</Label>
                <Input type="number" value={form.positionX} onChange={(e) => setField("positionX", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Pos Z</Label>
                <Input type="number" value={form.positionZ} onChange={(e) => setField("positionZ", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Height</Label>
                <Input type="number" value={form.height} onChange={(e) => setField("height", e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Width</Label>
                <Input type="number" value={form.width} onChange={(e) => setField("width", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-display uppercase tracking-wide text-xs">Depth</Label>
                <Input type="number" value={form.depth} onChange={(e) => setField("depth", e.target.value)} />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.isActive} onChange={(e) => setField("isActive", e.target.checked)} className="h-4 w-4 accent-[#FFCE24]" />
              <span className="font-display uppercase tracking-wide text-xs text-[#2B2E34]">Visible in the world</span>
            </label>
          </div>

          <SheetFooter className="absolute bottom-0 left-0 right-0 bg-white border-t p-4" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="font-display uppercase tracking-wide">Cancel</Button>
            <Button data-testid="admin-save-building-button" onClick={save} disabled={saving} className="font-display uppercase tracking-wide">
              {saving ? "Saving…" : editing ? "Save changes" : "Create building"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
